import { supabase } from '../lib/supabase'
import type { Goal, GoalContribution, DbGoal, DbGoalContribution } from '../types'

type GoalRow = DbGoal & { goal_contributions: { amount: number }[] }

function toGoal(row: GoalRow): Goal {
  const saved_amount = row.goal_contributions.reduce((sum, c) => sum + c.amount, 0)
  return {
    id: row.id,
    name: row.name,
    target_amount: row.target_amount,
    saved_amount,
    percentage: row.target_amount > 0 ? Math.min(100, (saved_amount / row.target_amount) * 100) : 0,
    emoji: row.emoji,
    deadline: row.deadline,
  }
}

export async function listGoals(householdId: string): Promise<Goal[]> {
  const { data, error } = await (supabase.from('goals') as any)
    .select('*, goal_contributions(amount)')
    .eq('household_id', householdId)
    .order('created_at') as { data: GoalRow[] | null; error: unknown }
  if (error) throw error
  return (data ?? []).map(toGoal)
}

export async function createGoal(
  householdId: string,
  payload: { name: string; target_amount: number; emoji?: string | null; deadline?: string | null },
): Promise<Goal> {
  const trimmed = payload.name.trim()
  if (trimmed.length === 0 || trimmed.length > 80)
    throw new Error('Nome deve ter entre 1 e 80 caracteres')
  if (payload.target_amount <= 0)
    throw new Error('Valor alvo deve ser maior que zero')

  const { data, error } = await (supabase.from('goals') as any)
    .insert({
      household_id: householdId,
      name: trimmed,
      target_amount: payload.target_amount,
      emoji: payload.emoji ?? null,
      deadline: payload.deadline ?? null,
    })
    .select('*')
    .single() as { data: DbGoal | null; error: unknown }
  if (error) throw error
  if (!data) throw new Error('Meta não criada')
  return { id: data.id, name: data.name, target_amount: data.target_amount, saved_amount: 0, percentage: 0, emoji: data.emoji, deadline: data.deadline }
}

export async function updateGoal(
  id: string,
  payload: { name?: string; target_amount?: number; emoji?: string | null; deadline?: string | null },
): Promise<void> {
  const update: Record<string, unknown> = {}
  if (payload.name !== undefined) {
    const trimmed = payload.name.trim()
    if (trimmed.length === 0 || trimmed.length > 80)
      throw new Error('Nome deve ter entre 1 e 80 caracteres')
    update.name = trimmed
  }
  if (payload.target_amount !== undefined) {
    if (payload.target_amount <= 0) throw new Error('Valor alvo deve ser maior que zero')
    update.target_amount = payload.target_amount
  }
  if ('emoji' in payload) update.emoji = payload.emoji ?? null
  if ('deadline' in payload) update.deadline = payload.deadline ?? null

  const { error } = await (supabase.from('goals') as any).update(update).eq('id', id)
  if (error) throw error
}

export async function deleteGoal(id: string): Promise<void> {
  const { error } = await (supabase.from('goals') as any).delete().eq('id', id)
  if (error) throw error
}

export async function listContributions(goalId: string): Promise<GoalContribution[]> {
  const { data, error } = await (supabase.from('goal_contributions') as any)
    .select('id, goal_id, amount, date, note')
    .eq('goal_id', goalId)
    .order('date', { ascending: false }) as { data: DbGoalContribution[] | null; error: unknown }
  if (error) throw error
  return (data ?? []).map(row => ({
    id: row.id,
    goal_id: row.goal_id,
    amount: row.amount,
    date: row.date,
    note: row.note,
  }))
}

export async function addContribution(
  goalId: string,
  amount: number,
  date: string,
  note?: string | null,
): Promise<GoalContribution> {
  if (amount <= 0) throw new Error('Valor deve ser maior que zero')

  const { data, error } = await (supabase.from('goal_contributions') as any)
    .insert({ goal_id: goalId, amount, date, note: note ?? null })
    .select('id, goal_id, amount, date, note')
    .single() as { data: DbGoalContribution | null; error: unknown }
  if (error) throw error
  if (!data) throw new Error('Contribuição não registrada')
  return { id: data.id, goal_id: data.goal_id, amount: data.amount, date: data.date, note: data.note }
}

export async function deleteContribution(id: string): Promise<void> {
  const { error } = await (supabase.from('goal_contributions') as any).delete().eq('id', id)
  if (error) throw error
}
